import axios from 'axios';
import {
  createSearchParams,
  useLocation,
  useNavigate,
  matchPath,
} from 'react-router-dom';

import {
  ApiStatus,
  AppRoutes,
  AuthPages,
  SearchParams,
  userProfileAPI,
} from '@constants';
import { getAccessToken, is, isStatusFailedAny } from '@utils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { auth } from 'services/firebase';
import { User } from 'firebase/auth';
import { customLoginUser } from '@redux/auth';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { authSelectors } from '@redux/auth';

export const useAxiosConfig = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthInitialized, setIsAuthInitialized] = useState(false);
  const [fbUser, setFbUser] = useState<User | null>(null);
  // Custom login hooks and functionality
  const dispatch = useAppDispatch();
  const { apiStatus: customLoginApiStatus } = useAppSelector(
    authSelectors.getCustomLoginAPIData,
  );
  const customEmail = useMemo(
    () => new URLSearchParams(location.search).get('customEmail'),
    [location.search],
  );

  // Run custom login
  useEffect(() => {
    if (!customEmail || is.prod) return;
    dispatch(customLoginUser(customEmail));
  }, [customEmail, dispatch]);

  // Common auth hooks and functionality
  useEffect(
    () =>
      // first time auth is initialized, we set it to true, since FB for some reason needs ~500ms to initialize
      // Without this, we get a race condition where 'auth.currentUser?.getIdToken()' is not set in time and we get a 401
      auth.onAuthStateChanged((fbUserLocal: User | null) => {
        if (fbUserLocal) setFbUser(fbUserLocal);

        if (
          // If it's normal login flow - wait when FB is initialized
          !customEmail ||
          // If it's custom login flow - wait when custom login is complete
          customLoginApiStatus === ApiStatus.COMPLETE ||
          isStatusFailedAny(customLoginApiStatus)
        )
          setIsAuthInitialized(true);
      }),
    [customEmail, customLoginApiStatus],
  );

  const handleNavigateToSignIn = useCallback(() => {
    const isAuthPage = AuthPages.some((authPage) =>
      matchPath(authPage, location.pathname),
    );

    const navParams = {
      pathname: `/${AppRoutes.SIGN_IN}`,
      search: !isAuthPage
        ? createSearchParams({
            [SearchParams.REDIRECT]: `${location.pathname}${location.search}`,
          }).toString()
        : '',
    };

    navigate(navParams);
  }, [location.pathname, location.search, navigate]);

  axios.defaults.baseURL = is.localhost
    ? `${process.env.REACT_APP_API_URL}/api`
    : '/api';

  axios.interceptors.request.clear();
  axios.interceptors.response.clear();

  axios.interceptors.request.use(async (config) => {
    const token = await getAccessToken();

    if (token) {
      config.headers['x-user-token'] = token;
    }

    return config;
  });

  axios.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
      const originalRequest = error.config;

      if (originalRequest.url.includes(userProfileAPI)) {
        handleNavigateToSignIn();
      }

      return Promise.reject(error);
    },
  );

  return {
    isAuthInitialized,
    fbUser,
  };
};
